import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Phone, 
  Clock, 
  Shield, 
  CheckCircle, 
  ArrowRight, 
  Headphones, 
  Mic, 
  Volume2, 
  Activity,
  Timer
} from 'lucide-react';
import { Link } from 'react-router-dom';

const LauraInsuranceCallsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Laura, Insurance Calling Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Laura, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI Insurance Calling Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Laura handles the frustrating task of calling insurance payers. She navigates IVR menus, waits on hold, and speaks with representatives to verify benefits and check claim status.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Main Interface Card */}
                    <div className="bg-slate-900 rounded-[2.5rem] p-6 shadow-2xl border border-slate-700 relative z-10 overflow-hidden">
                        {/* Status Bar */}
                        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-800">
                             <div className="flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                                     <Phone className="w-5 h-5 text-green-400" />
                                 </div>
                                 <div>
                                     <div className="font-bold text-white">Live Call</div>
                                     <div className="text-xs text-slate-400">Delta Dental of CA</div>
                                 </div>
                             </div>
                             <div className="text-xl font-mono text-green-400 font-bold">04:12</div>
                        </div>

                        {/* Audio Visualization */}
                        <div className="h-32 flex items-center justify-center gap-1 mb-8">
                             {[...Array(20)].map((_, i) => (
                                 <div 
                                    key={i}
                                    className="w-1.5 bg-blue-500 rounded-full animate-pulse"
                                    style={{ 
                                        height: `${Math.random() * 100}%`,
                                        animationDelay: `${i * 0.05}s`,
                                        animationDuration: '0.8s'
                                    }}
                                 ></div>
                             ))}
                        </div>

                        {/* Transcript Area */}
                        <div className="space-y-4 mb-4">
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center font-bold text-xs text-slate-300">R</div>
                                <div className="bg-slate-800 p-3 rounded-2xl rounded-tl-none text-sm text-slate-300 max-w-[85%]">
                                    Can you verify the patient's date of birth and subscriber ID?
                                </div>
                            </div>
                            <div className="flex gap-3 flex-row-reverse">
                                <div className="w-8 h-8 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center">
                                    <Mic className="w-4 h-4 text-white" />
                                </div>
                                <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-sm text-white max-w-[85%]">
                                    Certainly. DOB is January 15th, 1985. ID is XJ992811.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Stats */}
                    <div className="absolute -right-6 top-12 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                                <Clock className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Hold Time</div>
                                <div className="text-lg font-bold text-slate-900">45 mins</div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute -left-6 bottom-20 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float" style={{ animationDelay: '1s' }}>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">IVR Navigation</div>
                                <div className="text-lg font-bold text-slate-900">Automated</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The End of Hold Music
            </h2>
            <p className="text-lg text-gray-600">
              Your staff spends hours every week listening to elevator music. Laura takes that burden off their shoulders.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Unlimited Patience</h3>
              <p className="text-gray-600">
                Laura can wait on hold for 5 minutes or 5 hours. She never gets frustrated, hangs up, or multitasks poorly.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Phone className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">IVR Navigation</h3>
              <p className="text-gray-600">
                "Press 1 for claims..." Laura intelligently navigates complex phone trees to reach the right department every time.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Notes</h3>
              <p className="text-gray-600">
                Every call is recorded, transcribed, and summarized directly into the patient's chart in Asha PMS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <Activity className="w-4 h-4" />
                Live Execution
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Laura Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Simply flag a patient or claim in your dashboard, and Laura initiates the call immediately.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Initiates Call</h4>
                    <p className="text-slate-400 text-sm">Dials the payer's provider line automatically.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Navigates Menu</h4>
                    <p className="text-slate-400 text-sm">Uses keypad tones and voice commands to reach a representative.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Converses with Rep</h4>
                    <p className="text-slate-400 text-sm">Provides NPI, Tax ID, and patient details to get answers.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Reports Back</h4>
                    <p className="text-slate-400 text-sm">Updates the task status and attaches the transcript.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center animate-pulse">
                              <Phone className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Live Call</div>
                              <div className="text-xs text-slate-400">Delta Dental of CA</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-green-400">04:12</div>
                  </div>

                  {/* Waveform Visualization */}
                  <div className="flex items-center justify-center gap-1 h-12 mb-8">
                      {[1, 3, 2, 5, 3, 7, 4, 6, 3, 5, 2, 4, 2, 3, 1, 2, 4, 3, 5, 2].map((h, i) => (
                          <div
                              key={i}
                              className="w-1 bg-blue-500 rounded-full animate-pulse"
                              style={{ height: `${h * 15}%`, animationDelay: `${i * 0.05}s` }}
                          ></div>
                      ))}
                  </div>

                  <div className="space-y-4">
                      {/* Rep Msg */}
                      <div className="flex gap-4">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex-shrink-0 flex items-center justify-center font-bold text-xs text-slate-300">R</div>
                          <div className="bg-slate-700 p-3 rounded-2xl rounded-tl-none text-sm text-slate-200">
                              Can you verify the patient's date of birth?
                          </div>
                      </div>

                      {/* AI Msg */}
                      <div className="flex gap-4 flex-row-reverse">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex-shrink-0 flex items-center justify-center">
                              <Mic className="w-4 h-4 text-white" />
                          </div>
                          <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-sm text-white">
                              Yes, it is January 15th, 1985.
                          </div>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">20 hrs</div>
              <div className="text-gray-600 font-medium">Saved Weekly</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Notes Accuracy</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">$0</div>
              <div className="text-gray-600 font-medium">Overtime Costs</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Get your staff off the phone.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Laura handle the hold times so your team can handle the patients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Hire Laura Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LauraInsuranceCallsPage;
